import { TIPOLOGIASERVIZIO } from '@core/utils/const';
import { ActivityTypeEnum } from '../distribuzione-censimento/attivita/activity-wizard/models/wizard.models';
import { rrulestr, Frequency } from 'rrule';
import {
  TipoRicorrenza,
  TipoRicorrenzaMensile,
} from 'src/app/shared/calendario-rrule/calendario-rrule.component';

/**
 * Returns the matching ActivityTypeEnum based on idTipologiaServizio from TIPOLOGIASERVIZIO
 */
export function getActivityTypeFromTipologia(
  idTipologiaServizio?: number | null,
): ActivityTypeEnum | null {
  if (idTipologiaServizio == null) {
    return null;
  }
  switch (idTipologiaServizio) {
    case TIPOLOGIASERVIZIO.porta_porta:
      return ActivityTypeEnum.RACCOLTA_PORTA_A_PORTA;
    case TIPOLOGIASERVIZIO.spazzamento:
      return ActivityTypeEnum.SPAZZAMENTO_E_LAVAGGIO;
    case TIPOLOGIASERVIZIO.controllo_periodico:
      return ActivityTypeEnum.CONTROLLO_PERIODICO;
    case TIPOLOGIASERVIZIO.isole:
      return ActivityTypeEnum.RACCOLTA_ISOLE_ECOLOGICHE;
    case TIPOLOGIASERVIZIO.on_demand:
      return ActivityTypeEnum.SERVIZI_ON_DEMAND;
    default:
      return null;
  }
}

/**
 * Parses time string (e.g. "06:00" or "06:00:00") into Date
 */
function parseTimeString(timeStr: string | null | undefined): Date | null {
  if (!timeStr) return null;
  try {
    const parts = timeStr.split(':');
    if (parts.length >= 2) {
      const d = new Date();
      d.setHours(Number(parts[0]), Number(parts[1]), 0, 0);
      return d;
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Maps a service detail DTO to wizard prefilled data according to the activity type.
 * Unpopulated fields: 'nome' and 'note' (must be null/empty for user input).
 */
export function mapServizioToWizardData(
  servizio: any,
  activityType: ActivityTypeEnum,
): any {
  switch (activityType) {
    case ActivityTypeEnum.RACCOLTA_PORTA_A_PORTA:
      return mapServizioPapToWizardData(servizio);
    case ActivityTypeEnum.SPAZZAMENTO_E_LAVAGGIO:
    case ActivityTypeEnum.CONTROLLO_PERIODICO:
    case ActivityTypeEnum.RACCOLTA_ISOLE_ECOLOGICHE:
    case ActivityTypeEnum.SERVIZI_ON_DEMAND:
    default:
      return mapServizioPapToWizardData(servizio);
  }
}

/**
 * Specific mapper for Porta a Porta service duplication
 */
function mapServizioPapToWizardData(dto: any): any {
  if (!dto) return {};

  // 1. Dati Generali (nome and note must NOT be prefilled)
  const eerCodes: number[] = Array.isArray(dto.frazioni)
    ? dto.frazioni.map((f: any) => f.codEer)
    : [];

  const percorsoId =
    dto.idTipologiaPercorso ?? dto.percorso?.idTipologiaPercorso ?? null;
  const percorsoName = dto.percorso?.descrizione || '';

  const serviziDatiGenerali = {
    nome: null,
    configurazionePercorso: percorsoId,
    _configurazionePercorsoName: percorsoName,
    EER: eerCodes,
    note: null,
  };

  // 2. Utenze Coinvolte
  const idTipologiaZona =
    dto.tipologieZone && dto.tipologieZone.length > 0
      ? dto.tipologieZone[0].idTipologiaZone
      : null;

  const zoneIds: number[] = [];
  if (Array.isArray(dto.tipologieZone)) {
    dto.tipologieZone.forEach((tz: any) => {
      if (Array.isArray(tz.zone)) {
        tz.zone.forEach((z: any) => {
          if (z.idZona !== undefined && z.idZona !== null) {
            zoneIds.push(z.idZona);
          }
        });
      }
    });
  }

  const classificazioni = Array.isArray(dto.classificazioni)
    ? dto.classificazioni.map((c: any) => c.codClassificazione)
    : [];

  const categorie = Array.isArray(dto.categorie)
    ? dto.categorie.map((cat: any) => cat.idCategoria)
    : [];

  const stati = Array.isArray(dto.statiUtenza)
    ? dto.statiUtenza.map((s: any) => s.idStato)
    : [];

  const zonaLabel = Array.isArray(dto.tipologieZone)
    ? dto.tipologieZone
        .flatMap((tz: any) => (tz.zone || []).map((z: any) => z.nome))
        .filter(Boolean)
        .join(', ')
    : '';

  const classificazioneLabel = Array.isArray(dto.classificazioni)
    ? dto.classificazioni
        .map((c: any) => c.descrizione)
        .filter(Boolean)
        .join(', ')
    : '';

  const categoriaLabel = Array.isArray(dto.categorie)
    ? dto.categorie
        .map((c: any) => c.descrizione)
        .filter(Boolean)
        .join(', ')
    : '';

  const statoLabel = Array.isArray(dto.statiUtenza)
    ? dto.statiUtenza
        .map((s: any) => s.descrizione)
        .filter(Boolean)
        .join(', ')
    : '';

  const serviziUtenzeCoinvolte = {
    idTipologiaZona: idTipologiaZona,
    zone: zoneIds,
    classificazione: classificazioni,
    categoria: categorie,
    stato: stati,
    includiEER: !!dto.filtraPerContenitore,
    _previewGenerated: false,
    _formValid: false,
    _isDuplicazione: true,
    _selectedZonaName: zonaLabel,
    _selectedClassificazioneName: classificazioneLabel,
    _selectedCategoriaName: categoriaLabel,
    _selectedStatoName: statoLabel,
  };

  // 3. Calendario
  const cal =
    dto.calendarioCorrente ||
    (Array.isArray(dto.calendari) && dto.calendari.length > 0
      ? dto.calendari[0]
      : null);

  let tipoRicorrenza: TipoRicorrenza = 'DAILY';
  let tipoRicorrenzaMensile: TipoRicorrenzaMensile = 'GIORNO_MESE';
  let posizioneMese = 1;
  let giornoSettimanaMensile = 'MO';
  let giorniMese: number[] = [];
  let giorniSettimana: string[] = [];
  let intervallo = 1;

  if (cal && cal.rrule) {
    try {
      const rruleObj = rrulestr(cal.rrule);
      const opts = rruleObj.options;

      if (opts.freq === Frequency.WEEKLY) tipoRicorrenza = 'WEEKLY';
      else if (opts.freq === Frequency.MONTHLY) tipoRicorrenza = 'MONTHLY';

      if (tipoRicorrenza === 'WEEKLY' && opts.byweekday?.length) {
        const wdays = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
        giorniSettimana = opts.byweekday.map((w: number) => wdays[w]);
      }

      if (tipoRicorrenza === 'MONTHLY') {
        if (opts.bynweekday?.length) {
          tipoRicorrenzaMensile = 'GIORNO_SETTIMANA';
          const [wday, n] = opts.bynweekday[0];
          const wdays = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
          giornoSettimanaMensile = wdays[wday];
          posizioneMese = n;
        } else if (opts.bymonthday?.length) {
          tipoRicorrenzaMensile = 'GIORNO_MESE';
          giorniMese = [...opts.bymonthday];
        }
      }
      intervallo = opts.interval || 1;
    } catch (e) {
      console.error('Errore parsing rrule in duplicazione servizio:', e);
    }
  }

  const dataInizio = cal?.dtstart ? new Date(cal.dtstart) : null;
  const dataFine = cal?.dtendRicorrenza ? new Date(cal.dtendRicorrenza) : null;
  const orarioInizio = parseTimeString(cal?.orarioInizio);
  const orarioFine = parseTimeString(cal?.orarioFine);

  const serviziCalendario = {
    nome: cal?.nome || 'Calendario di default',
    dataInizioValidita: dataInizio,
    dataFineValidita: dataFine,
    orarioInizio: orarioInizio,
    orarioFine: orarioFine,
    tipoRicorrenza,
    intervallo,
    giorniSettimana,
    tipoRicorrenzaMensile,
    posizioneMese,
    giornoSettimanaMensile,
    giorniMese,
    escludiSabato: cal ? !cal.rispettaSabato : false,
    escludiDomenica: cal ? !cal.rispettaDomenica : false,
    escludiFestivita: cal ? !cal.rispettaFestivita : false,
    calendario: cal
      ? {
          dtstart: cal.dtstart || null,
          dtend_ricorrenza: cal.dtendRicorrenza || null,
          orario_inizio: cal.orarioInizio || null,
          orario_fine: cal.orarioFine || null,
          rrule: cal.rrule || '',
          rispetta_sabato: !!cal.rispettaSabato,
          rispetta_domenica: !!cal.rispettaDomenica,
          rispetta_festivita: !!cal.rispettaFestivita,
        }
      : null,
  };

  // 4. Configurazione Operativa
  const firstVeicolo =
    Array.isArray(dto.veicoli) && dto.veicoli.length > 0
      ? dto.veicoli[0]
      : null;
  const idVeicolo = firstVeicolo
    ? (firstVeicolo.idVeicolo ?? firstVeicolo)
    : null;
  const veicoloTarga = firstVeicolo
    ? firstVeicolo.targa || `ID: ${idVeicolo}`
    : null;

  const operatoriIds = Array.isArray(dto.operatori)
    ? dto.operatori.map((o: any) => o.idOperatore ?? o)
    : [];
  const operatoriLabel = Array.isArray(dto.operatori)
    ? dto.operatori
        .map((o: any) => o.nome || o.nominativo || `ID: ${o.idOperatore ?? o}`)
        .join(', ')
    : null;

  const serviziConfigurazioneOperativa = {
    generaOds: !!dto.generaOdsAutomaticamente,
    giorniOds: dto.finestraGenerazioneOdsGiorni ?? 2,
    veicoloPrevisto: idVeicolo,
    operatoriPrevisti: operatoriIds,
    esitoDefault: dto.esitoDefaultSenzaLettura || 'SERVITA_SENZA_LETTURA',
    veicoloLabel: veicoloTarga,
    operatoriLabel: operatoriLabel,
  };

  return {
    serviziDatiGenerali,
    serviziUtenzeCoinvolte,
    serviziCalendario,
    serviziConfigurazioneOperativa,
  };
}
